import React, { useEffect, useState } from 'react';
import { FileType, Save, Sliders } from 'lucide-react';
import { estimateFileSize } from '../../utils/imageUtils';

interface OutputControlsProps {
  resizeMethod: string;
  format: string;
  quality: number;
  onFormatChange: (format: string) => void;
  onQualityChange: (quality: number) => void;
  currentDimensions?: { width: number; height: number } | null;
}

const formatOptions = [
  { value: 'image/jpeg', label: 'JPEG', description: 'Best for photos' },
  { value: 'image/png', label: 'PNG', description: 'Best for graphics with transparency' },
  { value: 'image/webp', label: 'WebP', description: 'Modern format, best compression' }
];

export function OutputControls({
  resizeMethod,
  format,
  quality,
  onFormatChange,
  onQualityChange,
  currentDimensions
}: OutputControlsProps) {
  const [estimatedSize, setEstimatedSize] = useState<string | null>(null);

  useEffect(() => {
    const updateEstimatedSize = async () => {
      if (currentDimensions) {
        const size = await estimateFileSize(
          currentDimensions.width,
          currentDimensions.height,
          format,
          quality
        );
        setEstimatedSize(size);
      }
    };
    updateEstimatedSize();
  }, [currentDimensions, format, quality]);

  return (
    <div className="bg-slate-50 dark:bg-slate-950/60 rounded-2xl border border-slate-200/80 dark:border-slate-800 p-4 space-y-4">
      <div className="flex items-center gap-2 border-b border-slate-100 dark:border-slate-800 pb-2">
        <Save className="w-4 h-4 text-indigo-500" />
        <h3 className="text-xs font-bold uppercase tracking-wider text-slate-800 dark:text-slate-200">
          Output Settings
        </h3>
      </div>

      <div className="space-y-3">
        {/* Format Selection */}
        <div className="space-y-1.5">
          <label className="block text-xs font-bold text-slate-700 dark:text-slate-300">
            Output Format
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {formatOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => onFormatChange(option.value)}
                className={`p-2.5 rounded-xl border text-left transition-all ${
                  format === option.value
                    ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-950/70 text-indigo-900 dark:text-indigo-200 ring-2 ring-indigo-500/30'
                    : 'border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 hover:border-indigo-400'
                }`}
              >
                <div className="flex items-center gap-1.5 mb-0.5">
                  <FileType className="w-4 h-4 text-indigo-500 flex-shrink-0" />
                  <span className="font-bold text-xs">{option.label}</span>
                </div>
                <span className="text-[10px] text-slate-500 dark:text-slate-400 block leading-tight">
                  {option.description}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Quality Slider (for JPEG and WebP) */}
        {(format === 'image/jpeg' || format === 'image/webp') && resizeMethod === 'dimension' && (
          <div className="space-y-1.5 pt-1">
            <div className="flex justify-between items-center text-xs font-bold text-slate-700 dark:text-slate-300">
              <label className="flex items-center gap-1.5">
                <Sliders className="w-3.5 h-3.5 text-indigo-500" />
                <span>Quality</span>
              </label>
              <span className="font-mono text-indigo-600 dark:text-indigo-400">{quality}%</span>
            </div>
            <input
              type="range"
              min="1"
              max="100"
              value={quality}
              onChange={(e) => onQualityChange(Number(e.target.value))}
              className="w-full accent-indigo-600 cursor-pointer h-2"
            />
          </div>
        )}

        {/* Estimated File Size Display */}
        {estimatedSize && (
          <div className="flex justify-between items-center text-[11px] text-slate-600 dark:text-slate-400 bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 px-3 py-2 rounded-xl">
            <span>Estimated output size:</span>
            <strong className="font-mono text-indigo-600 dark:text-indigo-400 font-bold">{estimatedSize}</strong>
          </div>
        )}
      </div>
    </div>
  );
}